import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Clock, Target, Video, FileText, ChevronRight } from 'lucide-react';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { formatTurkishDueDate } from '../utils/assignment';
import { WeeklyCalendarModal } from './WeeklyCalendarModal';
import type { AssignmentRow, LiveSessionRow } from '../types/sinif';

interface UpcomingAssignmentsCardProps {
  assignments: AssignmentRow[];
  liveSessions?: LiveSessionRow[];
  classId?: string;
  isTeacher?: boolean;
}

export function UpcomingAssignmentsCard({
  assignments,
  liveSessions = [],
  classId,
  isTeacher,
}: UpcomingAssignmentsCardProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const now = new Date();
  const nowTs = now.getTime() - 24 * 60 * 60 * 1000; // allow today's assignments

  // Yaklaşan (tamamlanmamış ve süresi geçmemiş veya bugün olan) ödevler
  const upcoming = assignments
    .filter(a => a.status !== 'tamamlandi' && a.due_date && new Date(a.due_date).getTime() >= nowTs)
    .sort((x, y) => new Date(x.due_date!).getTime() - new Date(y.due_date!).getTime());

  return (
    <>
      <Card className="p-5 space-y-4 shadow-sm">
        <h3 className="text-xs font-black uppercase tracking-widest text-text-secondary border-b border-border pb-3 flex items-center gap-2">
          <Calendar size={16} className="text-accent-500 shrink-0" /> YAKLAŞAN ÖDEVLER
        </h3>

        {upcoming.length === 0 ? (
          <p className="text-xs text-text-muted text-center py-4 font-medium">
            Yaklaşan ödevin yok.
          </p>
        ) : (
          <div className="space-y-2.5">
            {upcoming.slice(0, 3).map(a => {
              const isTest = a.type === 'test';
              const isVid = a.type === 'video';

              return (
                <div
                  key={a.id}
                  className="p-3 rounded-2xl bg-surface border border-border space-y-1 hover:border-accent-400 transition-colors"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] font-bold text-text-secondary flex items-center gap-1">
                      {isVid ? <Video size={11} /> : isTest ? <Target size={11} /> : <FileText size={11} />}
                      {isTest ? 'Test Ödevi' : isVid ? 'Video' : 'Ödev'}
                    </span>
                    <Badge variant="warning">
                      <Clock size={10} /> {formatTurkishDueDate(a.due_date)}
                    </Badge>
                  </div>

                  <p className="font-bold text-xs text-text-primary line-clamp-1">{a.title}</p>

                  {classId && (
                    <Link
                      to={`/sinif/${classId}/odev/${a.id}`}
                      className="text-[11px] font-bold text-accent-600 hover:underline flex items-center gap-1 pt-0.5"
                    >
                      Detay Gör <ChevronRight size={12} />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <Button
          variant="outline"
          onClick={() => setIsModalOpen(true)}
          className="w-full justify-center gap-2 font-bold text-xs py-2.5 rounded-2xl"
        >
          <Calendar size={15} /> Haftalık Takvimi Aç
        </Button>
      </Card>

      {/* Haftalık Takvim Modalı */}
      <WeeklyCalendarModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        assignments={assignments}
        liveSessions={liveSessions}
        classId={classId}
        isTeacher={isTeacher}
      />
    </>
  );
}
